import { toolbarBackdropClass } from '@y42/canvas-components';
import { GoJsModelProperties } from '@y42/gojs';
import { TextAlignment } from '@y42/json-schema';
import { isNullish } from '@y42/util/nullish';
import * as go from 'gojs';
import { Point } from '../../../models/point.interface';
import { dragCreatingLinkToolAPI } from '../../../tools/drag-create-link-tool/drag-creating-link-tool-api';
import { dragCreatingToolAPI } from '../../../tools/drag-create-tool/drag-creating-tool-api';
import { freeHandDrawingToolAPI } from '../../../tools/free-hand-drawing-tool/free-hand-drawing-tool-api';
import { mouseCapturingToolAPI } from '../../../tools/mouse-capturing-tool/mouse-capturing-tool-api';
import { getScreenCenterPoint } from '../../../utils/get-screen-center-point';
import { sidebarConfig, sidebarLinkCategory, sidebarNodeTypes } from '../config/config';
import { GojsFont } from '../models/gojs-font.model';
import { attentionNodeAPI } from '../templates/attention-node/api/attention-node-api';
import { emojiNodeAPI } from '../templates/emoji-node/api/emoji-node-api';
import { freeDrawNodeAPI } from '../templates/free-draw/api/free-draw-node-api';
import { imageNodeAPI } from '../templates/image-node/api/image-node-api';
import { linkAPI } from '../templates/link/api/link-api';
import { linkTemplate } from '../templates/link/link';
import { sidebarLinkingToolIsValidFrom, sidebarLinkingToolIsValidTo } from '../templates/link/utils/linking-utils';
import { nodeGroupNodeAPI } from '../templates/node-group-node/api/node-group-node-api';
import { pinNodeAPI } from '../templates/pin-node/api/pin-node-api';
import { shapeNodeAPI } from '../templates/shape-node/api/shape-node-api';
import { stickyNodeAPI } from '../templates/sticky-node/api/sticky-node-api';
import { textNodeAPI } from '../templates/text-node/api/text-node-api';
import { LinkArchetype } from './../templates/link/models/link.model';

export const createShapeOnDiagram = (diagram: go.Diagram, svgPath: string) => {
  if (isNullish(diagram)) {
    console.error('No diagram!');
    return;
  }

  dragCreatingToolAPI.run(
    diagram,
    {
      fill: sidebarConfig.colors.shapeNode.defaultBackground,
      stroke: sidebarConfig.colors.shapeNode.defaultBorderColor,
      geometryString: `f ${svgPath}`,
      opacity: 0.5,
    },
    shapeNodeAPI().getCursor(),
    toolbarBackdropClass,
    async ({ x, y, width, height }: go.Rect) => {
      const node = await shapeNodeAPI().create(diagram, {
        defaultBackground: sidebarConfig.colors.shapeNode.defaultBackground,
        defaultBorderColor: sidebarConfig.colors.shapeNode.defaultBorderColor,
        defaultLocked: sidebarConfig.shapeNode.defaultLocked,
        defaultShadow: sidebarConfig.shapeNode.defaultShadow,
        width,
        height,
        geometryString: `f ${svgPath}`,
        categoryName: 'shapeNode',
        location: { x, y },
      });

      if (node) {
        diagram.select(node);
      }
    },
  );
};

export const createLinkOnDiagram = (diagram: go.Diagram) => {
  dragCreatingLinkToolAPI.run(
    diagram,
    new LinkArchetype(sidebarLinkCategory),
    linkAPI().getCursor(),
    toolbarBackdropClass,
    linkTemplate(),
    sidebarLinkingToolIsValidFrom,
    sidebarLinkingToolIsValidTo,
  );
};

export const createFreeHandDrawingOnDiagram = (diagram: go.Diagram, borderWidth = 5) => {
  freeHandDrawingToolAPI.run(
    diagram,
    {
      strokeWidth: borderWidth,
    },
    freeDrawNodeAPI().getCursor(),
    toolbarBackdropClass,
    async ({ x, y }: go.Point, geoString: string) => {
      const node = await freeDrawNodeAPI().create(diagram, {
        defaultBorderColor: sidebarConfig.colors.freeDrawNode.defaultBorderColor,
        defaultLocked: sidebarConfig.freeDrawNode.defaultLocked,
        geometryString: geoString,
        categoryName: sidebarNodeTypes.freeDraw,
        location: { x, y },
        borderWidth,
      });

      diagram.select(node);
    },
  );
};

export const createTextOnDiagram = (diagram: go.Diagram) => {
  const hasDarkMode = diagram.model.modelData[GoJsModelProperties.HasDarkMode];

  if (isNullish(diagram)) {
    console.error('No diagram!');
    return;
  }

  mouseCapturingToolAPI.run(diagram, textNodeAPI().getCursor(), toolbarBackdropClass, (location: Point) => {
    diagram.commit(async () => {
      const node = await textNodeAPI().create(diagram, {
        categoryName: sidebarNodeTypes.textNode,
        defaultFont: sidebarConfig.textNode.defaultFont as GojsFont,
        defaultTextAlignment: sidebarConfig.textNode.defaultTextAlignment as TextAlignment,
        defaultTextColor: hasDarkMode
          ? sidebarConfig.colors.textNode.darkModeTextColor
          : sidebarConfig.colors.textNode.defaultTextColor,
        defaultTextHighlightColor: sidebarConfig.colors.textNode.defaultTextHighlightColor,
        defaultShadow: sidebarConfig.textNode.defaultShadow,
        defaultLocked: sidebarConfig.textNode.defaultLocked,
        location,
      });

      diagram.select(node);
    });
  });
};

export const createImageOnDiagram = (diagram: go.Diagram, imgUrl: string) => {
  if (isNullish(diagram)) {
    console.error('No diagram!');
    return;
  }
  const { x, y } = getScreenCenterPoint(diagram);

  imageNodeAPI().create(diagram, {
    categoryName: sidebarNodeTypes.imageNode,
    source: imgUrl,
    location: { x, y },
  });
};

export const createStickyOnDiagram = (diagram: go.Diagram, color: string) => {
  if (isNullish(diagram)) {
    console.error('No diagram!');
    return;
  }
  mouseCapturingToolAPI.run(diagram, stickyNodeAPI().getCursor(), toolbarBackdropClass, async (location: Point) => {
    const sticky = await stickyNodeAPI().create(diagram, {
      categoryName: sidebarNodeTypes.stickyNode,
      defaultBackground: color,
      defaultTextColor: sidebarConfig.colors.stickyNode.defaultTextColor,
      width: sidebarConfig.stickyNode.width,
      height: sidebarConfig.stickyNode.height,
      defaultShadow: sidebarConfig.stickyNode.defaultShadow,
      defaultFont: sidebarConfig.stickyNode.defaultFont as GojsFont,
      location,
    });

    diagram.select(sticky);
  });
};

export const createAttentionNodeOnDiagram = (diagram: go.Diagram) => {
  if (isNullish(diagram)) {
    console.error('No diagram!');
    return;
  }

  mouseCapturingToolAPI.run(diagram, attentionNodeAPI().getCursor(), toolbarBackdropClass, (location: Point) => {
    diagram.commit(() => {
      attentionNodeAPI().create(diagram, {
        categoryName: 'attentionNode',
        location,
      });
    });
  });
};

export const createNodeGroupOnDiagram = (diagram: go.Diagram) => {
  if (isNullish(diagram)) {
    console.error('No diagram!');
    return;
  }

  mouseCapturingToolAPI.run(diagram, nodeGroupNodeAPI().getCursor(), toolbarBackdropClass, (location: Point) => {
    diagram.commit(() => {
      nodeGroupNodeAPI().create(diagram, {
        categoryName: sidebarNodeTypes.nodeGroupNode,
        location,
        width: sidebarConfig.nodeGroupNode.width,
        height: sidebarConfig.nodeGroupNode.height,
      });
    });
  });
};

export const createPinOnDiagram = (diagram: go.Diagram) => {
  if (isNullish(diagram)) {
    console.error('No diagram!');
    return;
  }

  mouseCapturingToolAPI.run(diagram, pinNodeAPI().getCursor(), toolbarBackdropClass, (location: Point) => {
    diagram.commit(() => {
      pinNodeAPI().create(diagram, {
        categoryName: sidebarNodeTypes.pinNode,
        location,
      });
    });
  });
};

export const createEmojiOnDiagram = (diagram: go.Diagram, emoji: string) => {
  if (isNullish(diagram)) {
    console.error('No diagram!');
    return;
  }

  mouseCapturingToolAPI.run(diagram, emojiNodeAPI().getCursor(emoji), toolbarBackdropClass, (location: Point) => {
    diagram.commit(() => {
      emojiNodeAPI().create(diagram, {
        categoryName: sidebarNodeTypes.emojiNode,
        location,
        emoji,
      });
    });
  });
};
